'use client';

/**
 * Toast — transient floating notification (theme T5c §3).
 *
 * Called imperatively via the `useToast()` hook anywhere under
 * <ToastProvider>:
 *
 *     const toast = useToast()
 *     toast.success('Saved')
 *     toast.error('Couldn\'t upload', { description: '...' })
 *
 * Auto-dismiss durations per T5c §3.3.  Hover pauses the timer.
 * Positioned bottom-right on desktop; bottom edge full-width on mobile.
 */
import { CheckCircle2, Info, TriangleAlert, XOctagon } from 'lucide-react';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { cn } from './cn';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastOptions {
  description?: ReactNode;
  /** Override the default auto-dismiss ms for this toast. */
  durationMs?: number;
  /** Optional action rendered as a tertiary text button (e.g. "Undo"). */
  action?: { label: string; onClick: () => void };
}

interface ToastRecord {
  id: number;
  variant: ToastVariant;
  title: ReactNode;
  description?: ReactNode;
  durationMs: number;
  action?: ToastOptions['action'];
}

interface ToastApi {
  info: (title: ReactNode, options?: ToastOptions) => void;
  success: (title: ReactNode, options?: ToastOptions) => void;
  warning: (title: ReactNode, options?: ToastOptions) => void;
  error: (title: ReactNode, options?: ToastOptions) => void;
  dismiss: (id: number) => void;
}

const DEFAULT_DURATION_MS: Record<ToastVariant, number> = {
  info: 4000,
  success: 4000,
  warning: 6000,
  error: 8000,
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number): void => {
    setToasts((curr) => curr.filter((t) => t.id !== id));
  }, []);

  const enqueue = useCallback(
    (variant: ToastVariant) =>
      (title: ReactNode, options: ToastOptions = {}): void => {
        const id = ++counter.current;
        const durationMs = options.durationMs ?? DEFAULT_DURATION_MS[variant];
        setToasts((curr) =>
          [
            {
              id,
              variant,
              title,
              description: options.description,
              durationMs,
              action: options.action,
            },
            ...curr,
          ].slice(0, 3),
        );
      },
    [],
  );

  const api = useRef<ToastApi>({
    info: enqueue('info'),
    success: enqueue('success'),
    warning: enqueue('warning'),
    error: enqueue('error'),
    dismiss,
  });

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast() called outside <ToastProvider>. Add ToastProvider to app/layout.tsx.',
    );
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const VARIANTS: Record<ToastVariant, { stripe: string; iconClass: string; icon: typeof Info }> = {
  info: { stripe: 'bg-brand-primary-600', iconClass: 'text-brand-primary-600', icon: Info },
  success: { stripe: 'bg-success-600', iconClass: 'text-success-600', icon: CheckCircle2 },
  warning: {
    stripe: 'bg-brand-accent-600',
    iconClass: 'text-brand-accent-600',
    icon: TriangleAlert,
  },
  error: { stripe: 'bg-error-600', iconClass: 'text-error-600', icon: XOctagon },
};

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      aria-label="Notifications"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-toast flex w-full max-w-sm flex-col-reverse gap-2 md:bottom-6 md:right-6"
      role="region"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: () => void }) {
  const [paused, setPaused] = useState(false);
  const v = VARIANTS[toast.variant];
  const Icon = v.icon;

  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(onDismiss, toast.durationMs);
    return () => clearTimeout(timer);
  }, [paused, toast.durationMs, onDismiss]);

  return (
    <div
      className={cn(
        'pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-lg border border-slate-200 bg-white p-4 pl-5 shadow-5',
      )}
      role="status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', v.stripe)} />
      <Icon
        aria-hidden
        className={cn('mt-0.5 shrink-0', v.iconClass)}
        size={16}
        strokeWidth={1.75}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{toast.title}</p>
        {toast.description ? (
          <p className="mt-0.5 text-xs text-slate-600">{toast.description}</p>
        ) : null}
        {toast.action ? (
          <button
            className="mt-2 text-xs font-medium text-brand-primary-600 hover:text-brand-primary-700"
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss();
            }}
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      <button
        aria-label="Dismiss"
        className="shrink-0 rounded-md p-1 text-slate-400 transition-colors duration-fast ease-standard hover:bg-slate-100 hover:text-slate-700"
        type="button"
        onClick={onDismiss}
      >
        <X aria-hidden size={14} strokeWidth={1.75} />
      </button>
    </div>
  );
}
